import { CSSProperties } from 'react';

export const sxAppBar = {
  position: 'relative',
  bgcolor: 'grey.900',
  borderBottom: '1px solid rgba(var(--palette-grey-500Channel) / 0.16)',
};

export const sxTitle = {
  ml: 2,
  flex: 1,
  textAlign: 'left',
};

export const sxHeaderActions = {
  display: 'flex',
  gap: 1,
};

export const sxMobileFooter = {
  display: 'flex',
  gap: 1.5,
  p: 2,
  bgcolor: 'grey.900',
};

export const sxFooterActions = {
  display: 'flex',
  gap: 1.5,
  width: '100%',
};

export type ScanHighlightColor = 'success' | 'error';

export const sxVideoContainer = (highlightColor?: ScanHighlightColor) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  overflow: 'hidden',
  '& .scan-region-highlight': {
    borderRadius: '11px',
    outline: 'rgba(0, 0, 0, 0.48) solid 50vmax',
  },
  // !important is required: qr-scanner styles the highlight SVG inline (stroke:#e9b213),
  // and inline styles can only be overridden with !important
  '& .scan-region-highlight-svg': {
    stroke: `${highlightColor ? `var(--palette-${highlightColor}-main)` : '#ffffff'} !important`,
    transform: 'scale(1.035) !important',
  },
});

export const sxScannerModeOverlay = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  px: 3,
  textAlign: 'center',
  bgcolor: 'grey.900',
  color: 'text.secondary',
  zIndex: 5,
};

export const sxScannerPrompt = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  p: 4,
  width: '100%',
  maxWidth: 900,
  borderRadius: 2,
  border: '1px dashed',
  borderColor: 'divider',
};

export const sxScanFeedbackCard = (color: 'success' | 'error') => ({
  width: '100%',
  maxWidth: 900,
  textAlign: 'left',
  borderRadius: 2,
  border: '1px solid',
  borderColor: `${color}.main`,
  overflow: 'hidden',
});

export const sxScanFeedbackHeader = (color: 'success' | 'error') => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  px: 3,
  py: 2,
  bgcolor: `rgba(var(--palette-${color}-mainChannel) / 0.16)`,
});

export const sxScanFeedbackMain = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 3,
  p: 3,
};

export const sxScanFeedbackDetails = {
  minWidth: 0,
};

export const sxScanFeedbackValue = (highlighted: boolean) => ({
  color: highlighted ? 'success.main' : 'common.white',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const sxScanFeedbackImage = {
  width: '50%',
  maxWidth: 400,
  flexShrink: 0,
  alignSelf: 'flex-start',
  borderRadius: 1,
};

export const sxFeedbackChip = {
  position: 'absolute',
  left: '50%',
  top: 'calc(50% + 25vmin + 24px)',
  transform: 'translateX(-50%)',
  zIndex: 10,
};

export const sxTopControls = {
  position: 'absolute',
  top: 15,
  left: 20,
  right: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 10,
};

export const sxCounterChip = {
  bgcolor: 'white',
  color: 'grey.800',
};

export const sxThumbnailsContainer = {
  position: 'absolute',
  bottom: 20,
  left: 20,
  display: 'flex',
  gap: 1,
  maxWidth: 'calc(100% - 40px)',
  overflowX: 'auto',
  zIndex: 10,
};

export const sxThumbnailButton = {
  width: 60,
  height: 60,
  borderRadius: 1,
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
};

export const sxThumbnailValidatedImage = {
  opacity: 'var(--palette-action-disabledOpacity, 0.48)',
};

export const sxThumbnailValidatedBadge = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'common.white',
  pointerEvents: 'none',
};

export const sxThumbnailImage = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

export const videoStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

export const sxGalleryWrapper = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'common.black',
  zIndex: 20,
};

export const sxGalleryCarousel = {
  width: '100%',
  height: '100%',
};

export const sxGalleryCarouselContainer = {
  height: '100%',
};

export const sxGalleryCarouselSlide = {
  height: '100%',
  display: 'flex',
};

export const sxGallerySlide = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: 0,
};

export const sxGallerySlideImage = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
};

export const sxGalleryPrevArrow = {
  left: 8,
  transform: 'translateY(-50%)',
};

export const sxGalleryNextArrow = {
  right: 8,
  left: 'auto',
  transform: 'translateY(-50%)',
};

export const sxGalleryDots = {
  position: 'absolute',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 30,
};

export const sxGalleryTopLeft = {
  position: 'absolute',
  top: 20,
  left: 20,
  zIndex: 30,
};

export const sxCameraWrapper = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  zIndex: 40,
  bgcolor: 'common.black',
};

export const sxCapturedPreview = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};
